import { useMemo, useState } from "react"
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, ResizablePanel, cn } from "@duck/ui"
import { ArrowsPointingOutIcon } from "@heroicons/react/24/outline"
import { X, ZoomInIcon, ZoomOutIcon } from "lucide-react"
import { getPaneAtom, workspaceLayoutAtom } from "@/store/workspaceAtoms"
import { useSetAtom, useAtomValue } from "jotai"
import { persistPanesLayout, WorkspaceLayoutName, WorkspaceLayouts } from "../DataWorkspace/utils"
import { WorkspaceLayoutPane } from "@/components/DataWorkspace/utils"
import { MinimizePane } from "./MinimizePane"
import { useIsMobile } from "@duck/ui"

const zoom = (dir: "+" | "-") => (prevZoom: number): number => {
    return Math.max(Math.min(prevZoom + (dir === "+" ? 1 : -1) * 20, 200), 60)
}

const panesOrder: Record<WorkspaceLayoutPane, number> = {
    queryHistory: 1,
    schema: 2,
    query: 3,
    queryOutput: 4,
    assignment: 10,
    solution: 11,
    top: 20,
    bottom: 21,
}

interface WorkspacePaneProps<L extends WorkspaceLayoutName> {
    paneKey: WorkspaceLayoutPane
    layoutName: L
    children: React.ReactNode
    className?: string
    title: string
    minSize: number
    maxSize: number
    onToggle?: (open: boolean) => void
    onFullscreen?: (open: boolean) => void
    beforeFullscreen?: () => Promise<void>
    isLast?: boolean
    showContentZoom?: boolean
    titleActions?: React.ReactNode
    contentActions?: React.ReactNode
}

export const WorkspacePane = <L extends WorkspaceLayoutName>({
    paneKey,
    layoutName,
    children,
    className,
    title,
    minSize,
    maxSize,
    onToggle,
    onFullscreen,
    beforeFullscreen,
    isLast,
    titleActions,
    contentActions,
    showContentZoom
}: WorkspacePaneProps<L>) => {
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [zoomLevel, setZoomLevel] = useState(100)
    const isMobile = useIsMobile()
    const workspaceLayout = useAtomValue(workspaceLayoutAtom)
    const show = useAtomValue(getPaneAtom(paneKey))
    const setPaneState = useSetAtom(getPaneAtom(paneKey))
    const initialSize = useMemo(() => {
        const value = persistPanesLayout.get(layoutName)
        return value[paneKey as keyof WorkspaceLayouts[L]] as number
    }, [paneKey, layoutName])


    const zoomContent = show && showContentZoom && (
        <div className="absolute top-10 right-3 flex items-center px-1 py-0">
            <Button
                type="button"
                size="xs"
                variant="ghost"
                className="rounded-full"
                aria-label="Zoom out"
                onClick={() => setZoomLevel(zoom("-"))}
            >
                <ZoomOutIcon className="w-4 h-4" />
            </Button>
            <Button
                type="button"
                size="xs"
                variant="ghost"
                className="rounded-full"
                aria-label="Zoom in"
                onClick={() => setZoomLevel(zoom("+"))}
            >
                <ZoomInIcon className="w-4 h-4" />
            </Button>
        </div>
    )

    const isHorizontalCollapsed = !show && workspaceLayout === 'split-side'
    const isVerticalCollapsed = !show && workspaceLayout !== 'split-side'

    const paneContent = (
        <>
            <div className={cn("h-full flex flex-col overflow-hidden relative", className, isHorizontalCollapsed && "flex-row h-auto")} data-tour-id={`${paneKey}-pane`}>
                <div
                    className={cn(
                        "h-8 relative flex items-center justify-between text-sm font-semibold text-foreground ",
                        "bg-card px-3 py-2 border-b border-border [box-shadow:0_4px_6px_-2px_rgba(0,0,0,0.08),0_4px_6px_-1px_rgba(0,0,0,0.05)]",
                        isHorizontalCollapsed && "h-8 w-full flex-row items-center justify-center border-b border-r-0 border-l-0 px-3 py-2 cursor-pointer hover:bg-secondary/50 transition-colors",
                        isVerticalCollapsed && "h-full w-8 flex-col items-center justify-center border-r border-b-0 border-l-0 py-3 px-2 [writing-mode:vertical-rl] [text-orientation:mixed] cursor-pointer hover:bg-secondary/50 transition-colors"
                    )}
                    onClick={!show ? () => {
                        setPaneState(true)
                        onToggle?.(!show)
                    } : undefined}
                >
                    <span className={cn("flex-1", isHorizontalCollapsed && "text-center items-center")}>{title}</span>
                    {show && titleActions && (
                        <div className="flex items-center mr-2">
                            {titleActions}
                        </div>
                    )}
                    {show && (
                        <div className="flex items-center">
                            {!isMobile && <MinimizePane paneKey={paneKey} onClick={() => {
                                setPaneState(false)
                                onToggle?.(!show)
                            }} />}
                            <button
                                onClick={async (e) => {
                                    await beforeFullscreen?.()
                                    setIsFullscreen(true)
                                    onFullscreen?.(true)
                                }}
                                className="p-1.5 rounded hover:bg-secondary/70 transition-colors flex-shrink-0"
                                aria-label="Fullscreen"
                                title="Fullscreen"
                            >
                                <ArrowsPointingOutIcon className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
                {show && (
                    <div className="h-full flex flex-col overflow-auto custom-scrollbar bg-card/30" style={zoomLevel !== 100 ? { zoom: zoomLevel / 100 } : undefined}>
                        {children}
                    </div>
                )}
                {show && contentActions && (
                    <div className="absolute top-0 right-0">
                        {contentActions}
                    </div>
                )}
                {zoomContent}
            </div>

            {/* Fullscreen Dialog */}
            <Dialog open={isFullscreen} onOpenChange={(open) => {
                onFullscreen?.(open)
                setIsFullscreen(open)
            }}>
                <DialogContent className="sm:max-w-[95vw] sm:max-h-[95vh] sm:h-full flex flex-col p-0 gap-0 [&>button]:hidden">
                    <DialogHeader className="relative z-10 px-4 py-3 border-b border-border bg-secondary/50 max-h-13 [box-shadow:0_8px_12px_-2px_rgba(0,0,0,0.2),0_4px_6px_-1px_rgba(0,0,0,0.15)]">
                        <div className="flex items-center justify-between">
                            <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
                            <button
                                onClick={() => {
                                    setIsFullscreen(false)
                                    onFullscreen?.(false)
                                }}
                                className="p-1.5 rounded hover:bg-secondary/70 transition-colors"
                                aria-label="Exit fullscreen"
                                title="Exit fullscreen"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </DialogHeader>
                    <div className="relative flex-1 flex flex-col overflow-hidden h-full min-h-0">
                        {zoomContent}
                        <div className="flex-1 flex flex-col overflow-auto custom-scrollbar p-4 max-h-[calc(100vh-48px)]" style={zoomLevel !== 100 ? { zoom: zoomLevel / 100 } : undefined}>
                            {children}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )

    if (!show) {
        return paneContent
    }

    return (
        <ResizablePanel defaultSize={initialSize} minSize={minSize} maxSize={maxSize} id={paneKey} order={panesOrder[paneKey]}>
            {paneContent}
        </ResizablePanel>
    )
}